import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Support() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: '通知が届かない場合の対処方法',
      answer: (
        <div className="space-y-2">
          <p>以下の手順をお試しください：</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>iPhoneの「設定」を開く</li>
            <li>「通知」をタップ</li>
            <li>「ERAI」を選択</li>
            <li>「通知を許可」がオンになっているか確認</li>
            <li>アプリを再起動してみる</li>
          </ol>
          <p className="mt-2">それでも解決しない場合は、お問い合わせください。</p>
        </div>
      ),
    },
    {
      question: 'サブスクリプションの解約方法',
      answer: (
        <div className="space-y-2">
          <p>以下の手順で解約できます：</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>iPhoneの「設定」を開く</li>
            <li>画面上部のApple IDをタップ</li>
            <li>「サブスクリプション」をタップ</li>
            <li>「ERAI」を選択</li>
            <li>「サブスクリプションをキャンセルする」をタップ</li>
          </ol>
          <p className="mt-2">解約後も期間終了まで機能をご利用いただけます。</p>
        </div>
      ),
    },
    {
      question: 'データの削除方法',
      answer: (
        <div className="space-y-2">
          <p>データを削除するには：</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>アプリ内の「設定」を開く</li>
            <li>「データ管理」をタップ</li>
            <li>「すべてのデータを削除」を選択</li>
            <li>確認画面で「削除」をタップ</li>
          </ol>
          <p className="mt-2">※この操作は取り消せません。削除前にご確認ください。</p>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F0] via-[#FFE8F0] to-[#F5E8FF]">
      <div className="max-w-[600px] mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="https://static.readdy.ai/image/2ded901b8fb990e993c5117b32e5999e/f6b52d0828689e13d597e866faced30e.png"
              alt="ERAI Logo"
              className="w-16 h-16 rounded-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">ERAI（えらい）</h1>
          <p className="text-sm text-gray-500 mt-1">サポート</p>
        </div>

        {/* App Overview */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <div className="flex items-start">
            <span className="text-2xl mr-3">💙</span>
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-2">アプリについて</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                ERAIは毎日を肯定するAI通知アプリです。あなたの気分に寄り添い、前向きな言葉を届けます。
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4">よくある質問</h2>
          <div className="space-y-0">
            {faqs.map((faq, index) => (
              <div key={index}>
                {index > 0 && <div className="border-t border-gray-100 my-3" />}
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full text-left flex items-center justify-between py-2 cursor-pointer hover:opacity-70 transition-opacity"
                >
                  <div className="flex items-start flex-1">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-100 text-pink-600 text-xs font-semibold flex items-center justify-center mr-3 mt-0.5">
                      Q
                    </span>
                    <span className="text-sm font-semibold text-gray-800 leading-relaxed">
                      {faq.question}
                    </span>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform duration-300 flex-shrink-0 ml-2 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="bg-gray-50 rounded-lg p-4 ml-9 mt-2 text-sm text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4">お問い合わせ</h2>
          <div className="text-center py-4">
            <span className="text-4xl mb-3 block">📧</span>
            <p className="text-sm text-gray-600 mb-3">
              ご不明な点がございましたら、お気軽にお問い合わせください
            </p>
            <a
              href="mailto:poznan9320@gmail.com"
              className="inline-block text-[#5B9BD5] font-medium hover:underline px-3 py-2"
            >
              poznan9320@gmail.com
            </a>
          </div>
        </div>

        {/* Policy Links */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <Link
            to="/privacy-policy"
            className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            <div className="flex items-center">
              <span className="text-lg mr-3">📄</span>
              <span className="text-sm font-medium text-gray-700">プライバシーポリシー</span>
            </div>
            <i className="ri-arrow-right-s-line text-xl text-gray-400" />
          </Link>
          <div className="border-t border-gray-100 my-2" />
          <Link
            to="/terms"
            className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            <div className="flex items-center">
              <span className="text-lg mr-3">📄</span>
              <span className="text-sm font-medium text-gray-700">利用規約</span>
            </div>
            <i className="ri-arrow-right-s-line text-xl text-gray-400" />
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-xs text-gray-400">© 2025 ERAI</p>
          <p className="text-xs text-gray-400 mt-1">Made with 💙</p>
        </div>
      </div>
    </div>
  );
}